// StatsPanel.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Sparkles, Crown, Flame, Zap, Diamond, Gem, Lock } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const StatsPanel = ({ userId }) => {
  const [userStats, setUserStats] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [showMilestone, setShowMilestone] = useState(false);
  const progressBarRef = useRef(null);

  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        const userStatsRef = doc(db, "userStats", userId);
        const userStatsDoc = await getDoc(userStatsRef);
        if (userStatsDoc.exists()) {
          setUserStats(userStatsDoc.data());
          // Show milestone animation if close to next rank
          if (userStatsDoc.data().rank.progress > 90) {
            setShowMilestone(true);
          }
        }
      } catch (error) {
        console.error("Error fetching user stats:", error);
      }
    };

    fetchUserStats();
  }, [userId]);

  if (!userStats) return null;

  const isNearMilestone = userStats.rank.progress > 90;
  const getRankColor = () => {
    if (isHovered) return 'from-indigo-600 to-violet-600';
    return 'from-indigo-700 to-violet-700';
  };

  return (
    <motion.div 
      className="relative p-4 rounded-xl overflow-hidden backdrop-blur-sm border border-white/10"
      style={{
        background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.9) 100%)'
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Premium glass morphism background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-violet-500/5" />
        <motion.div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 20%, rgba(79, 70, 229, 0.15) 0%, transparent 30%),
              radial-gradient(circle at 80% 80%, rgba(124, 58, 237, 0.15) 0%, transparent 30%),
              radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)
            `,
          }}
          animate={{
            backgroundPosition: [
              '0% 0%, 0% 0%, 0% 0%',
              '100% 100%, -50% 50%, 50% -50%',
              '0% 0%, 0% 0%, 0% 0%'
            ]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        {/* Premium sparkling particles */}
        <div className="absolute inset-0 overflow-hidden">
          {Array.from({ length: 15 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.5
              }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0]
              }}
              transition={{
                duration: Math.random() * 2 + 1,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative flex gap-4 items-start">
        {/* Premium rank badge with 3D effect */}
        <motion.div 
          className={`relative rounded-2xl p-3.5 bg-gradient-to-br ${getRankColor()} shadow-lg transform perspective-1000`}
          whileHover={{ 
            scale: 1.05, 
            rotateY: [0, 15, 0, -15, 0],
            boxShadow: '0 20px 25px -5px rgba(79, 70, 229, 0.2), 0 10px 10px -5px rgba(124, 58, 237, 0.1)'
          }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.8 }}
        >
          {/* Reflective surface effect */}
          <motion.div 
            className="absolute inset-0 rounded-2xl opacity-70 bg-gradient-to-t from-transparent via-white/10 to-transparent" 
            animate={{ 
              background: isHovered 
                ? 'linear-gradient(to bottom, rgba(255,255,255,0.2), transparent 50%, rgba(255,255,255,0.1), transparent)'
                : 'linear-gradient(to bottom, rgba(255,255,255,0.1), transparent 30%, transparent)' 
            }}
            style={{ mixBlendMode: 'overlay' }}
          />
          
          <Diamond className="w-9 h-9 text-white drop-shadow-md" />
          
          {/* Dynamic glow effect with premium look */}
          <motion.div
            className="absolute inset-0 rounded-2xl"
            style={{ 
              background: isNearMilestone 
                ? 'radial-gradient(circle, rgba(192, 132, 252, 0.4) 0%, rgba(124, 58, 237, 0) 70%)' 
                : 'radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, rgba(124, 58, 237, 0) 70%)',
              filter: 'blur(8px)', 
              zIndex: -1 
            }}
            animate={{
              opacity: isNearMilestone ? [0.6, 1, 0.6] : [0.4, 0.6, 0.4],
              scale: isNearMilestone ? [0.8, 1.1, 0.8] : [0.9, 1, 0.9],
            }}
            transition={{
              duration: isNearMilestone ? 1.5 : 2.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />

          {/* Premium indicator for near milestone */}
          <AnimatePresence>
            {isNearMilestone && (
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 rounded-full"
              >
                <Zap className="w-3 h-3 text-white filter drop-shadow-lg" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <div className="flex-1 pt-1">
          {/* Premium points display with floating achievements */}
          <motion.div 
            className="flex items-center gap-2 mb-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="relative">
              <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 to-violet-200 drop-shadow-sm">
                {userStats.points.total}
              </span>
              <div
                className="absolute top-1 left-full ml-4 text-xs font-medium px-1.5 py-0.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white rounded-full flex items-center gap-1"
              >
                <span>+{userStats.points.daily}</span>
                <Flame className="w-3 h-3" />
              </div>
            </div>
            
            <motion.div
              animate={{
                rotate: [0, 15, -15, 0],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                repeatDelay: 3,
              }}
            >
              <Gem className="w-5 h-5 text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400" />
            </motion.div>
          </motion.div>

          {/* Premium progress track */}
          <div className="relative w-full h-2 rounded-full mb-2 overflow-hidden backdrop-blur-sm bg-black/20" ref={progressBarRef}>
            {/* Track background with glass effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-full" />
            
            {/* Progress gradient */}
            <motion.div
              className="absolute h-full rounded-full"
              style={{
                background: isNearMilestone 
                  ? 'linear-gradient(90deg, rgb(109, 40, 217) 0%, rgb(192, 132, 252) 70%, rgb(250, 204, 21) 100%)'
                  : 'linear-gradient(90deg, rgb(109, 40, 217) 0%, rgb(139, 92, 246) 100%)'
              }}
              initial={{ width: 0 }}
              animate={{ width: `${userStats.rank.progress}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
            
            {/* Premium shimmer effect */}
            <motion.div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%)'
              }}
              animate={{
                x: ['-100%', '100%'],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear",
              }}
            />
            
            {isNearMilestone && (
              <motion.div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, rgba(250, 204, 21, 0.3) 50%, transparent 100%)'
                }}
                animate={{
                  x: ['-100%', '100%'],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
            )}
            
            {/* Progress markers for premium feel */}
            {[25, 50, 75].map((marker) => (
              <div 
                key={marker} 
                className={`absolute top-0 bottom-0 w-px bg-white/20 ${userStats.rank.progress >= marker ? 'opacity-0' : 'opacity-100'}`} 
                style={{ left: `${marker}%` }} 
              />
            ))}
          </div>

          {/* Dynamic rank information with premium look */}
          <AnimatePresence mode="wait">
            {isHovered ? (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center text-sm font-medium"
              >
                <div className="mr-1.5 text-violet-400">
                  <Lock className="w-3.5 h-3.5" />
                </div>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-300 to-fuchsia-300">
                  Next: {userStats.rank.next}
                </span>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="flex items-center text-sm font-medium"
              >
                <div className="mr-1.5 text-violet-400">
                  <Crown className="w-3.5 h-3.5" />
                </div>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-300 to-fuchsia-300">
                  {userStats.rank.current}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Premium milestone notification */}
      <AnimatePresence>
        {showMilestone && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 20 }}
            className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-4 py-2 
                      bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-medium 
                      rounded-full shadow-lg flex items-center gap-2 backdrop-blur-sm"
            onClick={() => setShowMilestone(false)}
          >
            <div className="p-1 bg-white/20 rounded-full">
              <Star className="w-3.5 h-3.5" />
            </div>
            <span>Almost there! Next rank: {userStats.rank.next}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default React.memo(StatsPanel);


// // StatsPanel.jsx
// import React, { useState, useEffect, useRef } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { Trophy, Star, Sparkles, Crown, Flame, Zap } from 'lucide-react';
// import { doc, getDoc } from 'firebase/firestore';
// import { db } from '../../firebase';

// const StatsPanel = ({ userId }) => {
//   const [userStats, setUserStats] = useState(null);
//   const [isHovered, setIsHovered] = useState(false);
//   const [showMilestone, setShowMilestone] = useState(false);
//   const progressBarRef = useRef(null);

//   useEffect(() => {
//     const fetchUserStats = async () => {
//       try {
//         const userStatsRef = doc(db, "userStats", userId);
//         const userStatsDoc = await getDoc(userStatsRef);
//         if (userStatsDoc.exists()) {
//           setUserStats(userStatsDoc.data());
//           // Show milestone animation if close to next rank
//           if (userStatsDoc.data().rank.progress > 90) {
//             setShowMilestone(true);
//           }
//         }
//       } catch (error) {
//         console.error("Error fetching user stats:", error);
//       }
//     };

//     fetchUserStats();
//   }, [userId]);

//   if (!userStats) return null;

//   const rankBgColor = isHovered ? 'bg-emerald-500' : 'bg-emerald-600';
//   const isNearMilestone = userStats.rank.progress > 90;

//   return (
//     <motion.div 
//       className="relative p-4 rounded-xl bg-slate-900/50 overflow-hidden backdrop-blur-sm"
//       onHoverStart={() => setIsHovered(true)}
//       onHoverEnd={() => setIsHovered(false)}
//     >
//       {/* Dynamic particle system background */}
//       <motion.div
//         className="absolute inset-0"
//         style={{
//           background: `
//             radial-gradient(circle at 20% 20%, rgb(16 185 129 / 0.3) 0%, transparent 50%),
//             radial-gradient(circle at 80% 80%, rgb(16 185 129 / 0.2) 0%, transparent 40%),
//             radial-gradient(circle at 50% 50%, rgb(16 185 129 / 0.1) 0%, transparent 60%)
//           `
//         }}
//         animate={{
//           backgroundPosition: [
//             '0% 0%, 0% 0%, 0% 0%',
//             '100% 100%, -50% 50%, 50% -50%',
//             '0% 0%, 0% 0%, 0% 0%'
//           ]
//         }}
//         transition={{
//           duration: 15,
//           repeat: Infinity,
//           ease: "linear"
//         }}
//       />

//       <div className="relative flex gap-3 items-start">
//         {/* Interactive rank badge */}
//         <motion.div 
//           className={`relative rounded-2xl p-3 ${rankBgColor} transition-colors duration-300 cursor-pointer`}
//           whileHover={{ scale: 1.05, rotate: [0, -5, 5, 0] }}
//           whileTap={{ scale: 0.95 }}
//         >
//           <Trophy className="w-8 h-8 text-white" />
          
//           {/* Dynamic rank glow effect */}
//           <motion.div
//             className="absolute inset-0 rounded-2xl bg-emerald-400"
//             animate={{
//               opacity: isNearMilestone ? [0.4, 0.8, 0.4] : [0.2, 0.4, 0.2],
//               scale: isNearMilestone ? [1, 1.2, 1] : [1, 1.1, 1],
//             }}
//             transition={{
//               duration: isNearMilestone ? 1.5 : 2,
//               repeat: Infinity,
//               ease: "easeInOut"
//             }}
//             style={{ filter: 'blur(8px)', zIndex: -1 }}
//           />

//           {/* Level-up indicator for near milestone */}
//           <AnimatePresence>
//             {isNearMilestone && (
//               <motion.div
//                 initial={{ opacity: 0, scale: 0 }}
//                 animate={{ opacity: 1, scale: 1 }}
//                 exit={{ opacity: 0, scale: 0 }}
//                 className="absolute -top-2 -right-2"
//               >
//                 <Zap className="w-4 h-4 text-yellow-300 filter drop-shadow-lg" />
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </motion.div>

//         <div className="flex-1 pt-1">
//           {/* Points display with floating achievements */}
//           <motion.div 
//             className="flex items-center gap-2 mb-1"
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//           >
//             <span className="text-2xl font-bold text-white group relative">
//               {userStats.points.total}
//               <motion.span
//                 className="absolute -top-3 -right-2 text-xs text-emerald-400 opacity-0 group-hover:opacity-100"
//                 animate={{ y: [-20, -25, -20] }}
//                 transition={{ duration: 2, repeat: Infinity }}
//               >
//                 +{userStats.points.daily} today
//               </motion.span>
//             </span>
//             <motion.div
//               animate={{
//                 rotate: [0, 10, -10, 0],
//                 scale: [1, 1.2, 1],
//               }}
//               transition={{
//                 duration: 2,
//                 repeat: Infinity,
//                 repeatDelay: 3,
//               }}
//             >
//               <Sparkles className="w-5 h-5 text-emerald-400" />
//             </motion.div>
//           </motion.div>

//           {/* Enhanced progress bar */}
//           <div className="relative w-full bg-white/10 h-1.5 rounded-full mb-1 overflow-hidden" ref={progressBarRef}>
//             <motion.div
//               className="absolute h-full rounded-full"
//               style={{
//                 background: isNearMilestone 
//                   ? 'linear-gradient(90deg, rgb(16 185 129) 0%, rgb(252 211 77) 100%)'
//                   : 'linear-gradient(90deg, rgb(16 185 129) 0%, rgb(5 150 105) 100%)'
//               }}
//               initial={{ width: 0 }}
//               animate={{ width: `${userStats.rank.progress}%` }}
//               transition={{ duration: 1, ease: "easeOut" }}
//             />
            
//             {/* Multi-layer shimmer effect */}
//             <motion.div
//               className="absolute inset-0"
//               style={{
//                 background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)'
//               }}
//               animate={{
//                 x: ['-100%', '100%'],
//               }}
//               transition={{
//                 duration: 2,
//                 repeat: Infinity,
//                 ease: "linear",
//               }}
//             />
            
//             {isNearMilestone && (
//               <motion.div
//                 className="absolute inset-0"
//                 style={{
//                   background: 'linear-gradient(90deg, transparent 0%, rgba(252, 211, 77, 0.3) 50%, transparent 100%)'
//                 }}
//                 animate={{
//                   x: ['-100%', '100%'],
//                 }}
//                 transition={{
//                   duration: 1.5,
//                   repeat: Infinity,
//                   ease: "linear",
//                 }}
//               />
//             )}
//           </div>

//           {/* Dynamic rank information */}
//           <AnimatePresence mode="wait">
//             {isHovered ? (
//               <motion.div
//                 initial={{ opacity: 0, y: -10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: -10 }}
//                 className="flex items-center text-sm text-emerald-400"
//               >
//                 <Crown className="w-4 h-4 mr-1" />
//                 <span>Next Lvl: {userStats.rank.next}</span>
//               </motion.div>
//             ) : (
//               <motion.div
//                 initial={{ opacity: 0, y: 10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: 10 }}
//                 className="flex items-center text-sm text-emerald-400"
//               >
//                 <Crown className="w-4 h-4 mr-1" />
//                 <span>{userStats.rank.current}</span>
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </div>
//       </div>

//       {/* Achievement popup for milestones */}
//       <AnimatePresence>
//         {showMilestone && (
//           <motion.div
//             initial={{ opacity: 0, scale: 0.5, y: 20 }}
//             animate={{ opacity: 1, scale: 1, y: 0 }}
//             exit={{ opacity: 0, scale: 0.5, y: 20 }}
//             className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 
//                        bg-yellow-400/90 text-yellow-900 text-sm font-medium rounded-full shadow-lg
//                        flex items-center gap-1.5 backdrop-blur-sm"
//             onClick={() => setShowMilestone(false)}
//           >
//             <Star className="w-4 h-4" />
//             <span>Almost there! Next rank: {userStats.rank.next}</span>
//           </motion.div>
//         )}
//       </AnimatePresence>
//     </motion.div>
//   );
// };

// export default React.memo(StatsPanel);


// // Used in ChatHistoryPanel - Desktop Version (I think)
// import React, { useState, useEffect, useRef } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { Trophy, Star, Sparkles, Crown, Flame, Zap } from 'lucide-react';
// import { doc, getDoc } from 'firebase/firestore';
// import { db } from '../../firebase';

// const StatsPanel = ({ userId }) => {
//   const [userStats, setUserStats] = useState(null);
//   const [isHovered, setIsHovered] = useState(false);
//   const [showMilestone, setShowMilestone] = useState(false);
//   const progressBarRef = useRef(null);

//   useEffect(() => {
//     const fetchUserStats = async () => {
//       try {
//         const userStatsRef = doc(db, "userStats", userId);
//         const userStatsDoc = await getDoc(userStatsRef);
//         if (userStatsDoc.exists()) {
//           setUserStats(userStatsDoc.data());
//           // Show milestone animation if close to next rank
//           if (userStatsDoc.data().rank.progress > 90) {
//             setShowMilestone(true);
//           }
//         }
//       } catch (error) {
//         console.error("Error fetching user stats:", error);
//       }
//     };

//     fetchUserStats();
//   }, [userId]);

//   if (!userStats) return null;

//   const rankBgColor = isHovered ? 'bg-emerald-500' : 'bg-emerald-600';
//   const isNearMilestone = userStats.rank.progress > 90;

//   return (
//     <motion.div 
//       className="relative p-4 rounded-xl bg-slate-900/50 overflow-hidden backdrop-blur-sm"
//       onHoverStart={() => setIsHovered(true)}
//       onHoverEnd={() => setIsHovered(false)}
//     >
//       {/* Dynamic particle system background */}
//       <motion.div
//         className="absolute inset-0"
//         style={{
//           background: `
//             radial-gradient(circle at 20% 20%, rgb(16 185 129 / 0.3) 0%, transparent 50%),
//             radial-gradient(circle at 80% 80%, rgb(16 185 129 / 0.2) 0%, transparent 40%),
//             radial-gradient(circle at 50% 50%, rgb(16 185 129 / 0.1) 0%, transparent 60%)
//           `
//         }}
//         animate={{
//           backgroundPosition: [
//             '0% 0%, 0% 0%, 0% 0%',
//             '100% 100%, -50% 50%, 50% -50%',
//             '0% 0%, 0% 0%, 0% 0%'
//           ]
//         }}
//         transition={{
//           duration: 15,
//           repeat: Infinity,
//           ease: "linear"
//         }}
//       />

//       <div className="relative flex gap-3 items-start">
//         {/* Interactive rank badge */}
//         <motion.div 
//           className={`relative rounded-2xl p-3 ${rankBgColor} transition-colors duration-300 cursor-pointer`}
//           whileHover={{ scale: 1.05, rotate: [0, -5, 5, 0] }}
//           whileTap={{ scale: 0.95 }}
//         >
//           <Trophy className="w-8 h-8 text-white" />
          
//           {/* Dynamic rank glow effect */}
//           <motion.div
//             className="absolute inset-0 rounded-2xl bg-emerald-400"
//             animate={{
//               opacity: isNearMilestone ? [0.4, 0.8, 0.4] : [0.2, 0.4, 0.2],
//               scale: isNearMilestone ? [1, 1.2, 1] : [1, 1.1, 1],
//             }}
//             transition={{
//               duration: isNearMilestone ? 1.5 : 2,
//               repeat: Infinity,
//               ease: "easeInOut"
//             }}
//             style={{ filter: 'blur(8px)', zIndex: -1 }}
//           />

//           {/* Level-up indicator for near milestone */}
//           <AnimatePresence>
//             {isNearMilestone && (
//               <motion.div
//                 initial={{ opacity: 0, scale: 0 }}
//                 animate={{ opacity: 1, scale: 1 }}
//                 exit={{ opacity: 0, scale: 0 }}
//                 className="absolute -top-2 -right-2"
//               >
//                 <Zap className="w-4 h-4 text-yellow-300 filter drop-shadow-lg" />
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </motion.div>

//         <div className="flex-1 pt-1">
//           {/* Points display with floating achievements */}
//           <motion.div 
//             className="flex items-center gap-2 mb-1"
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//           >
//             <span className="text-2xl font-bold text-white group relative">
//               {userStats.points.total}
//               <motion.span
//                 className="absolute -top-3 -right-2 text-xs text-emerald-400 opacity-0 group-hover:opacity-100"
//                 animate={{ y: [-20, -25, -20] }}
//                 transition={{ duration: 2, repeat: Infinity }}
//               >
//                 +{userStats.points.daily} today
//               </motion.span>
//             </span>
//             <motion.div
//               animate={{
//                 rotate: [0, 10, -10, 0],
//                 scale: [1, 1.2, 1],
//               }}
//               transition={{
//                 duration: 2,
//                 repeat: Infinity,
//                 repeatDelay: 3,
//               }}
//             >
//               <Sparkles className="w-5 h-5 text-emerald-400" />
//             </motion.div>
//           </motion.div>

//           {/* Enhanced progress bar */}
//           <div className="relative w-full bg-white/10 h-1.5 rounded-full mb-1 overflow-hidden" ref={progressBarRef}>
//             <motion.div
//               className="absolute h-full rounded-full"
//               style={{
//                 background: isNearMilestone 
//                   ? 'linear-gradient(90deg, rgb(16 185 129) 0%, rgb(252 211 77) 100%)'
//                   : 'linear-gradient(90deg, rgb(16 185 129) 0%, rgb(5 150 105) 100%)'
//               }}
//               initial={{ width: 0 }}
//               animate={{ width: `${userStats.rank.progress}%` }}
//               transition={{ duration: 1, ease: "easeOut" }}
//             />
            
//             {/* Multi-layer shimmer effect */}
//             <motion.div
//               className="absolute inset-0"
//               style={{
//                 background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)'
//               }}
//               animate={{
//                 x: ['-100%', '100%'],
//               }}
//               transition={{
//                 duration: 2,
//                 repeat: Infinity,
//                 ease: "linear",
//               }}
//             />
            
//             {isNearMilestone && (
//               <motion.div
//                 className="absolute inset-0"
//                 style={{
//                   background: 'linear-gradient(90deg, transparent 0%, rgba(252, 211, 77, 0.3) 50%, transparent 100%)'
//                 }}
//                 animate={{
//                   x: ['-100%', '100%'],
//                 }}
//                 transition={{
//                   duration: 1.5,
//                   repeat: Infinity,
//                   ease: "linear",
//                 }}
//               />
//             )}
//           </div>

//           {/* Dynamic rank information */}
//           <AnimatePresence mode="wait">
//             {isHovered ? (
//               <motion.div
//                 initial={{ opacity: 0, y: -10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: -10 }}
//                 className="flex items-center text-sm text-emerald-400"
//               >
//                 <Crown className="w-4 h-4 mr-1" />
//                 <span>Next Lvl: {userStats.rank.next}</span>
//               </motion.div>
//             ) : (
//               <motion.div
//                 initial={{ opacity: 0, y: 10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: 10 }}
//                 className="flex items-center text-sm text-emerald-400"
//               >
//                 <Crown className="w-4 h-4 mr-1" />
//                 <span>{userStats.rank.current}</span>
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </div>
//       </div>

//       {/* Achievement popup for milestones */}
//       <AnimatePresence>
//         {showMilestone && (
//           <motion.div
//             initial={{ opacity: 0, scale: 0.5, y: 20 }}
//             animate={{ opacity: 1, scale: 1, y: 0 }}
//             exit={{ opacity: 0, scale: 0.5, y: 20 }}
//             className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 
//                        bg-yellow-400/90 text-yellow-900 text-sm font-medium rounded-full shadow-lg
//                        flex items-center gap-1.5 backdrop-blur-sm"
//             onClick={() => setShowMilestone(false)}
//           >
//             <Star className="w-4 h-4" />
//             <span>Almost there! Next rank: {userStats.rank.next}</span>
//           </motion.div>
//         )}
//       </AnimatePresence>
//     </motion.div>
//   );
// };

// export default React.memo(StatsPanel);

// import React, { useState, useEffect } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { Trophy, Star, Sparkles, Crown } from 'lucide-react';
// import { doc, getDoc } from 'firebase/firestore';
// import { db } from '../../firebase';

// const StatsPanel = ({ userId }) => {
//   const [userStats, setUserStats] = useState(null);
//   const [isHovered, setIsHovered] = useState(false);

//   useEffect(() => {
//     const fetchUserStats = async () => {
//       try {
//         const userStatsRef = doc(db, "userStats", userId);
//         const userStatsDoc = await getDoc(userStatsRef);
//         if (userStatsDoc.exists()) {
//           setUserStats(userStatsDoc.data());
//         }
//       } catch (error) {
//         console.error("Error fetching user stats:", error);
//       }
//     };

//     fetchUserStats();
//   }, [userId]);

//   if (!userStats) return null;

//   const rankBgColor = isHovered ? 'bg-emerald-500' : 'bg-emerald-600';

//   return (
//     <motion.div 
//       className="relative p-4 rounded-xl bg-slate-900/50 overflow-hidden"
//       onHoverStart={() => setIsHovered(true)}
//       onHoverEnd={() => setIsHovered(false)}
//     >
//       {/* Animated background gradient */}
//       <motion.div
//         className="absolute inset-0 opacity-30"
//         style={{
//           background: 'radial-gradient(circle at center, rgb(16 185 129 / 0.3) 0%, transparent 70%)',
//         }}
//         animate={{
//           scale: [1, 1.2, 1],
//           opacity: [0.2, 0.3, 0.2],
//         }}
//         transition={{
//           duration: 4,
//           repeat: Infinity,
//           ease: "easeInOut"
//         }}
//       />

//       <div className="relative flex gap-3 items-start">
//         <motion.div 
//           className={`relative rounded-2xl p-3 ${rankBgColor} transition-colors duration-300`}
//           whileHover={{ scale: 1.05 }}
//           whileTap={{ scale: 0.95 }}
//         >
//           <Trophy className="w-8 h-8 text-white" />
//           {/* Rank glow effect */}
//           <motion.div
//             className="absolute inset-0 rounded-2xl bg-emerald-400"
//             animate={{
//               opacity: [0.2, 0.4, 0.2],
//               scale: [1, 1.1, 1],
//             }}
//             transition={{
//               duration: 2,
//               repeat: Infinity,
//               ease: "easeInOut"
//             }}
//             style={{ filter: 'blur(8px)', zIndex: -1 }}
//           />
//         </motion.div>

//         <div className="flex-1 pt-1">
//           {/* Points counter with animation */}
//           <motion.div 
//             className="flex items-center gap-2 mb-1"
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.5 }}
//           >
//             <span className="text-2xl font-bold text-white">
//               {userStats.points.total}
//             </span>
//             <motion.div
//               animate={{
//                 rotate: [0, 10, -10, 0],
//                 scale: [1, 1.2, 1],
//               }}
//               transition={{
//                 duration: 2,
//                 repeat: Infinity,
//                 repeatDelay: 3,
//               }}
//             >
//               <Sparkles className="w-5 h-5 text-emerald-400" />
//             </motion.div>
//           </motion.div>

//           {/* Progress bar with shimmering effect */}
//           <div className="relative w-full bg-white/10 h-1.5 rounded-full mb-1 overflow-hidden">
//             <motion.div
//               className="absolute h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"
//               initial={{ width: 0 }}
//               animate={{ width: `${userStats.rank.progress}%` }}
//               transition={{ duration: 1, ease: "easeOut" }}
//             />
//             <motion.div
//               className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
//               animate={{
//                 x: ['-100%', '100%'],
//               }}
//               transition={{
//                 duration: 2,
//                 repeat: Infinity,
//                 ease: "linear",
//               }}
//             />
//           </div>

//           {/* Rank progress text with hover effect */}
//           <AnimatePresence>
//             {isHovered ? (
//               <motion.div
//                 initial={{ opacity: 0, y: -10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: -10 }}
//                 className="flex items-center text-sm text-emerald-400"
//               >
//                 <Crown className="w-4 h-4 mr-1" />
//                 <span>Next rank: {userStats.rank.next}</span>
//               </motion.div>
//             ) : (
//               <motion.div
//                 initial={{ opacity: 0, y: 10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: 10 }}
//                 className="flex items-center text-sm text-emerald-400"
//               >
//                 <span>{userStats.rank.progress}% progress</span>
//               </motion.div>
//             )}
//           </AnimatePresence>
//         </div>
//       </div>
//     </motion.div>
//   );
// };

// export default React.memo(StatsPanel);